import { useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import {
  DndContext,
  DragOverlay,
  useSensor,
  useSensors,
  PointerSensor,
  KeyboardSensor,
  TouchSensor,
  MouseSensor,
} from "@dnd-kit/core"
import { SortableContext } from "@dnd-kit/sortable"
import { KanbanColumn } from "./KanbanColumn"
import { KanbanItem } from "./KanbanItem"
import { Container, Dropdown, Nav, Table } from "react-bootstrap"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faEllipsisVertical } from "@fortawesome/free-solid-svg-icons"
import { Pagination } from "../../pages/Pagination"
import Tooltip from "@mui/material/Tooltip"
import { toast } from "sonner"
import nodata from "../../Utils/assets/img/leads_no_data_found_removebg.png"
import { checkTwilioIntegration, maskContactNumber } from "../helpers/commonhelper"
import useLocalStorageState from "use-local-storage-state"
import TwilioIntegrationForm from "../../Components/ivr/TwilioIntegrationForm"
import GlobalCallModal from "../../Components/ivr/GlobalCallModal"


export const Kanban = ({
  stages,
  canEdit_canDownload,
  leadsData,
  setLeadsData,
  onUpdateLead,
  onAddLead,
  onEditLead,
  // onFollowUpLead,
  onDeleteLead,
  currView,
  visibleColumns,
  filteredLeads,
  onOpenLeadDetails,
  offset,
  limit,
  pageCount,
  handlePageChange,
  setOffset,
  setLimit,
  headers,
  extras,
  mainGrant, //this will be the leads or deal permission object
  followupGrant, // since the kanbanitem also has the followuup thing
  tempFilterData,
  module_name,
  highlightId
}) => {
  // Determine the data to display based on filterData
  const dataToDisplay = tempFilterData !== null ? tempFilterData : leadsData
  const [customer, setCustomer] = useLocalStorageState("customer")
  // State for active dragging item and updating leads
  const [activeItem, setActiveItem] = useState(null)
  const [updatingLeads, setUpdatingLeads] = useState([])
  const [callingTo, setCallingTo] = useState(null)
  const [isCheckingTwilio, setIsCheckingTwilio] = useState(false);
  const [globalDiller, setGlobalDiller] = useState(false);
  const [showtwilioModal, setShowTwilioModal] = useState(false);
  const [module_id, setModuleId] = useState(null)
  const pickedUpTaskColumn = useRef(null)

  const [openRow, setOpenRow] = useState(null)
  // Configure drag and drop sensors
  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor),
    useSensor(PointerSensor),
    useSensor(KeyboardSensor)
  )

  // Transform leads data into stage-based structure for kanban view
  const leads = useMemo(() => {
    if (!Array.isArray(leadsData)) return {}

    return stages.reduce((acc, stage) => {
      const stageLeads =
        leadsData.find((item) => item.stage_id === stage.id)?.leads || []
      acc[stage.id] = stageLeads
      return acc
    }, {})
  }, [stages, leadsData])

  // Get all leads flattened for table view
  const allLeads = useMemo(() => {
    if (!Array.isArray(leadsData)) return []

    const allLeadsFlat = leadsData.reduce((acc, stageData) => {
      const leads =
        stageData.leads?.map((c) => ({
          ...c,
          stagename: stageData.stage_name,
        })) || []
      return [...acc, ...leads]
    }, [])

    return filteredLeads || allLeadsFlat
  }, [leadsData, filteredLeads])

  // Get dragging task data for accessibility announcements
  const getDraggingTaskData = (taskId, columnId) => {
    const tasksInColumn = leads[columnId] || []
    const taskPosition = tasksInColumn.findIndex(
      (task) => task.lead_id === taskId
    )
    const column = stages.find((col) => col.id === columnId)
    return {
      tasksInColumn,
      taskPosition,
      column,
    }
  }

  // for calling
  const handleGlobalCallChange = () => {
    setGlobalDiller(false);
    setModuleId(null)
  };

  const checkTwilio = async () => {
    if (isCheckingTwilio) return;
    setIsCheckingTwilio(true);
    try {
      const res = await checkTwilioIntegration(customer?.customer_details?.customer_id, headers, extras)
      if (res.status === "fail" || res.success === false) {
        setShowTwilioModal(true);
      }
      if (res.status === "success") {
        setGlobalDiller(true);
      }

    } catch (error) {
      console.error(error)
    } finally {
      setIsCheckingTwilio(false);
    }
  }

  // Check if element has draggable data
  const hasDraggableData = (element) => {
    if (!element) return false
    const data = element.data.current
    return data?.type === "Task"
  }

  // Accessibility announcements for drag operations
  const announcements = {
    onDragStart({ active }) {
      if (!hasDraggableData(active)) return
      if (active.data.current?.type === "Task") {
        pickedUpTaskColumn.current = active.data.current.columnId
        const { tasksInColumn, taskPosition, column } = getDraggingTaskData(
          active.id,
          pickedUpTaskColumn.current
        )
        return `Picked up Task ${active.data.current.task.contact_full_name
          } at position: ${taskPosition + 1} of ${tasksInColumn.length
          } in column ${column?.stage_name}`
      }
    },
    onDragOver({ active, over }) {
      if (!hasDraggableData(active) || !hasDraggableData(over)) return
      if (
        active.data.current?.type === "Task" &&
        over.data.current?.type === "Task"
      ) {
        const { tasksInColumn, taskPosition, column } = getDraggingTaskData(
          over.id,
          over.data.current.columnId
        )
        if (over.data.current.columnId !== pickedUpTaskColumn.current) {
          return `Task ${active.data.current.task.contact_full_name
            } was moved over column ${column?.stage_name} in position ${taskPosition + 1
            } of ${tasksInColumn.length}`
        }
        return `Task was moved over position ${taskPosition + 1} of ${tasksInColumn.length
          } in column ${column?.stage_name}`
      }
    },
    onDragEnd({ active, over }) {
      pickedUpTaskColumn.current = null
      if (!hasDraggableData(active) || !hasDraggableData(over)) return
      if (
        active.data.current?.type === "Task" &&
        over.data.current?.type === "Task"
      ) {
        const { tasksInColumn, taskPosition, column } = getDraggingTaskData(
          over.id,
          over.data.current.columnId
        )
        if (over.data.current.columnId !== active.data.current.columnId) {
          return `Task was dropped into column ${column?.stage_name
            } in position ${taskPosition + 1} of ${tasksInColumn.length}`
        }
        return `Task was dropped into position ${taskPosition + 1} of ${tasksInColumn.length
          } in column ${column?.stage_name}`
      }
    },
    onDragCancel({ active }) {
      pickedUpTaskColumn.current = null
      if (!hasDraggableData(active)) return
      return `Dragging ${active.data.current?.type} cancelled.`
    },
  }

  // Handle drag start event - set active item for overlay
  const handleDragStart = (event) => {
    const { active } = event
    if (!hasDraggableData(active)) return

    if (active.data.current?.type === "Task") {
      setActiveItem({
        ...active.data.current.task,
        columnId: active.data.current.columnId,
      })
    }
  }

  // Handle drag end event and update lead stage
  const handleDragEnd = async (event) => {
    const { active, over } = event
    setActiveItem(null)

    if (!over) return
    const activeId = active.id
    const overId = over.id

    if (!hasDraggableData(active)) return
    if (activeId === overId) return

    const activeData = active.data.current
    const overData = over.data.current

    if (activeData?.type !== "Task") return

    // Determine target column ID
    let targetColumnId
    if (overData?.type === "Task") {
      targetColumnId = overData.columnId
    } else {
      targetColumnId = over.id
    }

    const activeColumn = activeData.columnId

    if (activeColumn === targetColumnId) return

    // Find the dragged lead
    const draggedLead = leads[activeColumn]?.find(
      (lead) => lead.lead_id === activeId
    )
    if (!draggedLead) return

    // Update lead stage via parent component
    if (onUpdateLead) {
      setUpdatingLeads((prev) => [...prev, activeId])

      try {
        await onUpdateLead(draggedLead, targetColumnId, activeColumn)
      } catch (error) {
        // Error handling is done in parent component
      } finally {
        setUpdatingLeads((prev) => prev.filter((id) => id !== activeId))
      }
    }
  }

  // Handle drag over event for local reordering within same column
  const handleDragOver = (event) => {
    const { active, over } = event
    if (!over) return

    const activeId = active.id
    const overId = over.id

    if (activeId === overId) return
    if (!hasDraggableData(active)) return

    const activeData = active.data.current
    const overData = over.data.current

    if (activeData?.type !== "Task") return

    // Handle reordering within same column
    if (
      overData?.type === "Task" &&
      activeData.columnId === overData.columnId
    ) {
      const activeIndex =
        leads[activeData.columnId]?.findIndex((t) => t.lead_id === activeId) ??
        -1
      const overIndex =
        leads[overData.columnId]?.findIndex((t) => t.lead_id === overId) ?? -1

      if (activeIndex === -1 || overIndex === -1) return

      setLeadsData((prev) => {
        return prev.map((stageData) => {
          if (stageData.stage_id === activeData.columnId) {
            const newLeads = [...stageData.leads]
            const [removed] = newLeads.splice(activeIndex, 1)
            newLeads.splice(overIndex, 0, removed)
            return { ...stageData, leads: newLeads }
          }
          return stageData
        })
      })
    }
  }

  // Handle add lead click from column - passes stage ID to parent
  const handleAddClick = (stageId) => {
    onAddLead && onAddLead(stageId)
  }

  function totalBudget(array) {
    try {
      const parsedData = JSON.parse(array)
      const totalBudget = parsedData.reduce((total, item) => {
        return total + (parseFloat(item.budget) || 0)
      }, 0)
      return totalBudget
    } catch (error) {
      return 0
    }
  }

  // Get budget color based on value
  const getBudgetColor = (budgetValue) => {
    if (budgetValue < 10000) {
      return "#ffdfba" // Orange for less than 10,000
    } else if (budgetValue < 50000) {
      return "#d8dbff" // Purple for less than 50,000
    } else {
      return "#ace9ce" // Green for 50,000 and above
    }
  }

  // Get lead status capsule color
  const getLeadStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "lost":
        return { backgroundColor: "#ffd1d1", color: "#d63031" } // Light red background, dark red text
      case "inprogress":
        return { backgroundColor: "#d1e8ff", color: "#0984e3" } // Light blue background, dark blue text
      case "won":
        return { backgroundColor: "#d1f7d1", color: "#00b894" } // Light green background, dark green text
      case "new":
        return { backgroundColor: "#fff4d1", color: "#fdcb6e" } // Light yellow background, dark yellow text
      default:
        return { backgroundColor: "#f1f3f4", color: "#636e72" } // Light gray background, dark gray text
    }
  }
  const getDate = (dated) => {
    const date = new Date(dated)
    let formattedDate
    if (date) {
      const day = String(date.getDate()).padStart(2, "0")
      const month = String(date.getMonth() + 1).padStart(2, "0")
      const year = date.getFullYear()
      formattedDate = `${day}/${month}/${year}`
    }
    return formattedDate
  }
  // Get field display value for table view
  const getFieldValue = (lead, field) => {
    switch (field) {
      case "name":
        return lead.contact_full_name || "-"
      case "phone":
        // return loadPhoneNumber(lead.contact_phone)
        return <span onClick={() => {
          !showtwilioModal && setModuleId(lead?.lead_id)
          setCallingTo(loadPhoneNumber(lead?.contact_phone))
          checkTwilio();
        }} style={{ cursor: "pointer" }} >{loadPhoneNumber(lead?.contact_phone)}</span>
      case "email":
        return (
          (
            <>
              <i
                className="fa-solid fa-envelope"
                style={{ color: "black" }}
              ></i>{" "}
              {lead.contact_email}
            </>
          ) || "-"
        )
      case "expected_closure_date":
        return lead.expected_closure_date
          ? getDate(lead.expected_closure_date)
          : "-"
      // case "followup_date":
      //   return lead.followup_date ? getDate(lead.followup_date) : "-"
      case "requirement":
        return (
          { value: lead.requirement, length: lead.requirement?.length } || "-"
        )
      case "pipeline_stage_name":
        return lead.stage_name || "-"
      case "stagename":
        return lead.stagename || "-"
      case "service":
        return lead.service || "-"
      case "source":
        return (
          { value: lead.source_title, length: lead.source_title?.length } || "-"
        )
      // case "expected_closure_time":
      //   return lead.expected_closure_time || "-"
      // case "followup_time":
      //   return lead.followup_time || "-"
      case "lead_status":
        return lead.lead_status || "-"
      case "lead_owner":
        return lead.lead_owner_name || "-"
      default:
        return "-"
    }
  }

  function loadPhoneNumber(contact_phone) {
    if (!contact_phone) return "-"

    try {
      const parsed = JSON.parse(contact_phone)

      if (Array.isArray(parsed) && parsed.length > 0) {
        const { country_code, phone } = parsed[0]

        if (country_code && phone) {
          return (
            <span style={{ whiteSpace: "nowrap", letterSpacing: '0.25px', }}>
              +{country_code} {maskContactNumber(phone)}
            </span>
          )
        }
      }
    } catch (e) {
      return "-"
    }

    return "-"
  }


  // Reorder visible columns to place lead_status and budget after requirement
  // Reorder visible columns to place pipelineName, lead_status and budget after requirement
  const getOrderedVisibleColumns = () => {
    const loweredCaseCols = visibleColumns.map((c) => c?.toLowerCase()?.split(" ")?.join("_"))
    const baseColumns = [...loweredCaseCols]

    // Remove pipelineName, lead_status and budget if they exist
    const filteredColumns = baseColumns.filter(
      (col) =>
        col !== "pipeline_stage_name" && col !== "lead_status" && col !== "budget"
    )

    // Find requirement index
    const requirementIndex = filteredColumns.indexOf("requirement")

    // Insert pipelineName, lead_status and budget after requirement
    if (requirementIndex !== -1) {
      // Add pipelineName after requirement
      filteredColumns.splice(requirementIndex + 1, 0, "pipeline_stage_name")
      // Add lead_status after pipelineName
      filteredColumns.splice(requirementIndex + 2, 0, "lead_status")
      // Add budget after lead_status
      filteredColumns.splice(requirementIndex + 3, 0, "budget")
    } else {
      // If requirement not found, add at the end
      filteredColumns.push("pipeline_stage_name", "lead_status", "budget")
    }

    return filteredColumns
  }

  const orderedVisibleColumns = getOrderedVisibleColumns()

  // Get field display name for table headers
  const getFieldDisplayName = (field) => {
    const fieldNames = {
      name: "Name",
      phone: "Phone",
      email: "Email",
      budget: "Budget",
      lead_owner: "Lead Owner",
      expected_closure_date: "Expected Closure Date",
      // followup_date: "Follow Up Date",
      requirement: "Requirement",
      stagename: "Pipeline Stage Name", // Add this line
      pipeline_stage_name: "Pipeline Stage Name", // Also add this for consistency
      service: "Service",
      source: "Source",
      // expected_closure_time: "Expected Closure Time",
      // followup_time: "Follow Up Time",
      lead_status: "Lead Status",
    }
    return fieldNames[field] || field
  }

  let start = offset * limit + 1
  const effectivePageCount =
    tempFilterData !== null
      ? dataToDisplay?.length > 0
        ? 1
        : 0
      : pageCount;
  // Reset pagination when filtered data changes
  if (tempFilterData !== null && offset !== 0 && dataToDisplay?.length === 0) {
    setOffset(0);
  }
  return (
    <div>

      {
        currView === "card" ? (
          // Kanban Card View
          <DndContext
            accessibility={{ announcements }}
            sensors={sensors}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragOver={handleDragOver}
          >
            <div
              style={{
                overflowX: "auto",
                overflowY: "visible",
                padding: "0px 0",
                height: "calc(100vh - 11rem)",
                marginBottom: "0px",
                display: "flex",
              }}
            >
              <div
                className=" d-flex gap-2 listStyleForKanban"
                style={{ overflowY: "scroll", scrollBehavior: "smooth" }}
              >
                {stages.map((stage) => (
                  <SortableContext
                    key={stage.id}
                    items={(leads[stage.id] || []).map((i) => i.lead_id)}
                  >
                    {
                      mainGrant?.isView ?
                        <KanbanColumn
                          id={stage.id}
                          title={stage.stage_name}
                          items={leads[stage.id] || []}
                          onAddClick={handleAddClick}
                          updatingLeads={updatingLeads}
                          onEditLead={onEditLead}
                          // onFollowUpLead={onFollowUpLead}
                          onDeleteLead={onDeleteLead}
                          onOpenLeadDetails={onOpenLeadDetails}
                          stageColor={stage.color}
                          mainGrant={mainGrant}
                          followupGrant={followupGrant}
                          highlightId={highlightId}
                        />
                        :
                        <>You are not authorized to view the kanban view!</>
                    }
                  </SortableContext>
                ))}
              </div>
            </div>
            <style jsx>
              {`
              .listStyleForKanban::-webkit-scrollbar {
                height: 6px;
              }
              .listStyleForKanban::-webkit-scrollbar-track {
                background: white;
                border-radius: 10px;
              }
              .listStyleForKanban::-webkit-scrollbar-thumb {
                background: #e0e0e0;
                border-radius: 10px;
              }
              .listStyleForKanban::-webkit-scrollbar-thumb:hover {
                background: #c9c9c9;
              }
            `}
            </style>

            {/* Drag overlay for visual feedback during drag operations */}
            {createPortal(
              <DragOverlay>
                {activeItem && (
                  <KanbanItem
                    item={activeItem}
                    columnId={activeItem.columnId}
                    dragOverlay
                  />
                )}
              </DragOverlay>,
              document.body
            )}
          </DndContext>
        ) : (
          <>
            {globalDiller && (
              <GlobalCallModal
                isOpen={globalDiller}
                customerId={customer?.customer_details?.customer_id}
                loginUserId={customer?.customer_details?.id}
                phoneNumber={callingTo}
                onClose={handleGlobalCallChange}
                module_name={module_name}
                module_id={module_id}
              />
            )}

            {/* {twilio modal} */}
            {showtwilioModal && (
              <TwilioIntegrationForm
                isOpen={showtwilioModal}
                onClose={() => setShowTwilioModal(false)}
              />
            )}
            {/* // Table List View */}
            <Container
              fluid
              style={{
                fontFamily: "'Poppins', sans-serif",
                height: "calc(100vh - 12rem)",
                backgroundColor: "white",
                padding: 0,
                animation: "cardEntry 1s ease-out forwards",
              }}
              className="mt-3 p-0"
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  width: "100%",
                }}
              >
                <style>
                  {`
                
                         /* Fix for sticky headers */
                .container-table {
                  position: relative;
                }
                
                  .container-table thead {
                  position: sticky;
                  top: 0;
                  z-index: 10;
                  background-color: white;
                }
                
                .container-table thead th {
                  background-color: white;
                  border-bottom: 2px solid #dee2e6;
                }
              `}
                </style>

                <div
                  className="listStyleForContact container-table"
                  style={{
                    flex: allLeads?.length > 10 ? 1 : "auto",
                    overflow: "auto",
                    width: "100%",
                    maxHeight: allLeads?.length > 10 ? "100%" : "auto",
                    position: "relative",
                    scrollbarWidth: "thin"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.scrollbarColor = "#e0e0e0 white"

                  }}
                >
                  <Table
                    style={{
                      width: "100%",
                      tableLayout: "auto",
                      backgroundColor: "white",
                      marginBottom: "0px",
                      borderCollapse: "separate",
                      borderSpacing: "0px",
                    }}
                  >
                    <thead style={{ zIndex: 100 }}>
                      <tr style={{ textAlign: "start" }}>
                        <th
                          style={{
                            position: "sticky",
                            left: 0,
                            backgroundColor: "white",
                          }}
                        >
                          #
                        </th>
                        {orderedVisibleColumns.map((field) => (
                          <th
                            key={field}
                            style={{
                              position:
                                field === "name" ? "sticky" : "static",
                              left: field === "name" ? "40px" : "auto",
                              backgroundColor:
                                field === "name"
                                  ? "white"
                                  : "transparent",
                              minWidth:
                                field === "name" ? "200px" : "auto",
                            }}
                          >
                            {getFieldDisplayName(field)}{" "}
                            <i
                              className="fa-solid fa-sort"
                              style={{ fontSize: "0.6rem" }}
                            ></i>
                          </th>
                        ))}
                        <th
                          style={{
                            position: "sticky",
                            right: 0,
                            backgroundColor: "white",
                            zIndex: 10,
                          }}
                        ></th>
                      </tr>
                    </thead>

                    <tbody style={{ textAlign: "start" }} className="position-relative">
                      {
                        dataToDisplay?.length > 0 ?
                          dataToDisplay.map((lead, index) => {
                            const budgetValue = totalBudget(lead.product_services)
                            const budgetColor = getBudgetColor(budgetValue)
                            const leadStatusColors = getLeadStatusColor(
                              lead.lead_status
                            )

                            return (
                              <tr
                                key={lead.lead_id}
                                style={{ marginTop: "10px", marginBottom: "10px" }}
                                className={String(lead?.lead_id) === String(highlightId) ? "blink-row" : ""}
                              >
                                <td
                                  style={{
                                    position: "sticky",
                                    left: 0,
                                    backgroundColor: "white",
                                    zIndex: 90,
                                  }}
                                >
                                  {start + index}
                                </td>
                                {orderedVisibleColumns.map((field) => (
                                  < td
                                    key={field}
                                    style={{
                                      cursor:
                                        field === "name" && "pointer",
                                      fontWeight:
                                        field === "name" ? 600 : "normal",
                                      color:
                                        field === "phone"
                                          ? "#2184ff"
                                          : "inherit",
                                      width:
                                        field === "name" ? "16%" : "auto",
                                      textTransform:
                                        field === "name"
                                          ? "capitalize"
                                          : "inherit",
                                      position:
                                        field === "name"
                                          ? "sticky"
                                          : "static",
                                      left:
                                        field === "name" ? "40px" : "auto",
                                      backgroundColor:
                                        field === "name"
                                          ? "white"
                                          : "transparent",
                                      minWidth:
                                        field === "name"
                                          ? "200px"
                                          : "auto",
                                      maxWidth:
                                        field === "requirement" ? "280px" : "auto",
                                      overflow:
                                        field === "requirement" ? "hidden" : "visible",
                                      textOverflow:
                                        field === "requirement" ? "ellipsis" : "clip",
                                      whiteSpace:
                                        field === "phone" || field === "email"
                                          ? "nowrap"
                                          : field === "requirement"
                                            ? "nowrap"
                                            : "normal",
                                      textWrap: field === "lead_owner" && "nowrap"
                                    }}
                                    onClick={() =>
                                      field === "name" &&
                                      onOpenLeadDetails(lead)
                                    }
                                  >
                                    {field === "budget" ? (
                                      <div
                                        style={{
                                          display: "inline-flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          backgroundColor: budgetColor,
                                          color: "black",
                                          borderRadius: "12px",
                                          padding: "4px 12px",
                                          fontSize: "12px",
                                          fontWeight: "500",
                                          minWidth: "80px",
                                          textAlign: "center",
                                          height: "20px",
                                        }}
                                      >
                                        ₹{budgetValue.toLocaleString()}
                                      </div>
                                    ) : field === "lead_status" ? (
                                      <div
                                        style={{
                                          display: "inline-flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          backgroundColor:
                                            leadStatusColors.backgroundColor,
                                          color: leadStatusColors.color,
                                          borderRadius: "12px",
                                          padding: "4px 12px",
                                          fontSize: "12px",
                                          fontWeight: "500",
                                          minWidth: "80px",
                                          textAlign: "center",
                                          height: "20px",
                                        }}
                                      >
                                        {getFieldValue(lead, field)}
                                      </div>
                                    ) : field === "requirement" ? (
                                      getFieldValue(lead, field)?.length > 35 ? (
                                        <Tooltip
                                          title={getFieldValue(lead, field)?.value}
                                          arrow
                                          placement="bottom"
                                          componentsProps={{
                                            tooltip: {
                                              sx: {
                                                fontSize: "0.8rem",
                                                backgroundColor: "#7a3a5e",
                                                color: "white",
                                              },
                                            },
                                            arrow: {
                                              sx: {
                                                color: "#7a3a5e",
                                              },
                                            },
                                          }}
                                        >
                                          <div
                                            style={{
                                              cursor: "pointer",
                                              display: "inline-block",
                                              maxWidth: "100%",
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                              whiteSpace: "nowrap",
                                            }}
                                          >
                                            {getFieldValue(lead, field)?.value}
                                          </div>
                                        </Tooltip>
                                      ) : (
                                        <div
                                          style={{
                                            display: "inline-block",
                                            maxWidth: "100%",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          {getFieldValue(lead, field)?.value}
                                        </div>
                                      )
                                    ) :
                                      field === "source" ? (
                                        getFieldValue(lead, field)?.length > 20 ? (
                                          <Tooltip
                                            title={getFieldValue(lead, field).value}
                                            arrow
                                            placement="bottom"
                                            componentsProps={{
                                              tooltip: {
                                                sx: {
                                                  fontSize: "0.8rem",
                                                  backgroundColor: "#7a3a5e",
                                                  color: "white",
                                                },
                                              },
                                              arrow: {
                                                sx: {
                                                  color: "#7a3a5e",
                                                },
                                              },
                                            }}
                                          >
                                            <div
                                              style={{
                                                cursor: "pointer",
                                                display: "inline-block",
                                                maxWidth: "100%",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                              }}
                                            >
                                              {getFieldValue(lead, field).value?.slice(0, 30)}...
                                            </div>
                                          </Tooltip>
                                        ) : (
                                          <div
                                            style={{
                                              display: "inline-block",
                                              maxWidth: "100%",
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                              whiteSpace: "nowrap",
                                            }}
                                          >
                                            {getFieldValue(lead, field)?.value}
                                          </div>
                                        )
                                      ) :
                                        <span>{getFieldValue(lead, field)}</span>
                                    }
                                  </td>
                                ))}

                                <td
                                  style={{
                                    "--pad": "10px",
                                    paddingRight: "var(--pad) !important",
                                    paddingLeft: "var(--pad) !important",
                                    zIndex: openRow === lead.id ? 95 : 90,
                                  }}
                                >
                                  <div>
                                    <Dropdown
                                      as="div"
                                      className="dropdownsWithoutToggle"
                                      onToggle={(isOpen) =>
                                        setOpenRow(isOpen ? lead.id : null)
                                      }
                                    >
                                      <Dropdown.Toggle
                                        as={Nav.Link}
                                        className="header-link"
                                      >
                                        <FontAwesomeIcon
                                          icon={faEllipsisVertical}
                                          style={{
                                            fontSize: "15px",
                                            width: "20px",
                                            color: "#536485",
                                          }}
                                        />
                                      </Dropdown.Toggle>
                                      <Dropdown.Menu
                                        align={"end"}
                                        style={{
                                          width: "8rem",
                                          inset: "0px auto auto 0px",
                                          boxShadow: "rgb(215, 215, 215) 0px 0px 15px",
                                          position: "absolute",
                                          marginTop: "-22px",
                                          marginRight: "33px",
                                          minWidth: "3rem",
                                        }}
                                      >
                                        <Dropdown.Item
                                          style={{
                                            fontSize: "12px",
                                            padding: "4px 21px",
                                          }}
                                          className="d-flex align-items-center btn"
                                          onClick={(e) => {
                                            e.stopPropagation()
                                            if (!canEdit_canDownload) {
                                              toast.warning(
                                                "You are not authorised for this action!"
                                              )
                                            }
                                            onEditLead(lead.lead_id)
                                          }}
                                        >
                                          Edit
                                        </Dropdown.Item>
                                        <Dropdown.Item
                                          style={{
                                            fontSize: "12px",
                                            padding: "4px 21px",
                                          }}
                                          className="d-flex align-items-center btn"
                                          onClick={(e) => {
                                            if (!canEdit_canDownload) {
                                              toast.warning(
                                                "You are not authorised for this action!"
                                              )
                                            }
                                            e.stopPropagation()
                                            onDeleteLead(lead.lead_id)
                                          }}
                                        >
                                          Delete
                                        </Dropdown.Item>
                                      </Dropdown.Menu>
                                    </Dropdown>
                                  </div>
                                </td>
                              </tr>
                            )
                          })
                          :
                          (
                            <td
                              colSpan="14"
                              style={{ textAlign: "center", color: "gray" }}
                            >
                              <div
                                class="d-flex justify-content-center align-items-center"
                                style={{ position: "absolute", left: " 42%" }}
                              >
                                <img
                                  src={nodata}
                                  alt=""
                                  style={{ width: "17rem", marginTop: "0" }}
                                />
                              </div>
                            </td>
                          )
                      }
                      {dataToDisplay?.length > 10 && effectivePageCount > 1 && (
                        <>
                          <tr
                            className="no-hover-row"
                            style={{
                              backgroundColor: "#ffffff",
                              marginTop: "0px !important",
                              marginBottom: "0px !important",
                            }}
                          >
                            <td
                              colSpan={orderedVisibleColumns.length + 2}
                              style={{ backgroundColor: "#ffffff" }}
                            >
                              <div className="p-0">
                                <div
                                  className="d-flex justify-content-between align-items-center gap-2 mt-0"
                                  style={{ padding: "0.7rem 0" }}
                                >
                                  <div className="pagination-left">
                                    <p
                                      className="mb-0"
                                      style={{
                                        fontSize: "14px",
                                        paddingRight: "10rem",
                                      }}
                                    >
                                      Showing {offset + 1} of{" "}
                                      {effectivePageCount !== 0 ? effectivePageCount : effectivePageCount + 1}
                                    </p>
                                  </div>
                                  <div className="pagination-right d-flex gap-2 align-items-center">
                                    <select
                                      style={{ minWidth: "78px" }}
                                      id="limit"
                                      className="form-control form-select"
                                      onChange={(e) => {
                                        setLimit(Number(e.target.value))
                                        setOffset(0)
                                      }}
                                    >
                                      <option value={25}>25</option>
                                      <option value={100}>100</option>
                                      <option value={200}>200</option>
                                      <option value={300}>300</option>
                                    </select>
                                    <div className="d-flex align-items-center gap-3">
                                      <Pagination
                                        offset={offset}
                                        setOffset={setOffset}
                                        handlePageChange={handlePageChange}
                                        pageCount={effectivePageCount}
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        </>
                      )}
                    </tbody>
                  </Table>
                </div>

                {dataToDisplay?.length <= 10 && (
                  <div
                    style={{
                      padding: "0.5rem 0",
                      backgroundColor: "white",
                      borderTop: "1px solid #e0e0e0",
                      overflowX: "auto",
                      width: "100%",
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center gap-2">
                      <div className="pagination-left">
                        <p className="mb-0" style={{ fontSize: "14px" }}>
                          Showing {offset + 1} of{" "}
                          {effectivePageCount !== 0 ? effectivePageCount : effectivePageCount + 1}
                        </p>
                      </div>
                      <div className="pagination-right d-flex gap-2 align-items-center">
                        <select
                          style={{ minWidth: "78px" }}
                          onChange={(e) => {
                            setLimit(Number(e.target.value))
                            setOffset(0)
                          }}
                          id="limit"
                          className="form-control form-select"
                        >
                          <option value={25}>25</option>
                          <option value={100}>100</option>
                          <option value={200}>200</option>
                          <option value={300}>300</option>
                        </select>
                        <div className="d-flex align-items-center gap-3">
                          <Pagination
                            offset={offset}
                            setOffset={setOffset}
                            handlePageChange={handlePageChange}
                            pageCount={effectivePageCount}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </Container>
          </>
        )
      }
    </div >
  )
}

const styles = {
  lastdStyle: {
    padding: "0 !important",
  },
  lastactiontd: {
    padding: "0px !important",
  },
  lastactionTh: {
    padding: "0px !important",
  },
}
